import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, Search, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI } from '../services/api';
import toast from 'react-hot-toast';

interface QRScannerProps {
    onClose: () => void;
}

const QRScanner: React.FC<QRScannerProps> = ({ onClose }) => {
    const navigate = useNavigate();
    const [scanning, setScanning] = useState(false);
    const [loading, setLoading] = useState(false);
    const [manualInput, setManualInput] = useState('');
    const [error, setError] = useState('');
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const lookupInProgress = useRef(false);
    const containerRef = useRef<string>('qr-reader-' + Date.now());

    const lookupOrder = async (orderNumber: string) => {
        const trimmed = orderNumber.trim();
        if (!trimmed || lookupInProgress.current) return;

        lookupInProgress.current = true;
        setLoading(true);
        setError('');
        try {
            const res = await ordersAPI.lookupByOrderNumber(trimmed);
            if (res.data && res.data._id) {
                toast.success(`Order ${trimmed} found!`);
                stopScanner();
                onClose();
                navigate(`/orders/${res.data._id}`);
            } else {
                setError(`Order "${trimmed}" not found`);
                toast.error(`Order "${trimmed}" not found`);
                lookupInProgress.current = false;
            }
        } catch (err: any) {
            const msg = err.response?.data?.error || 'Order not found';
            setError(msg);
            toast.error(msg);
            lookupInProgress.current = false;
        } finally {
            setLoading(false);
        }
    };

    const startScanner = async (retryCount = 0) => {
        setError('');
        try {
            const scanner = new Html5Qrcode(containerRef.current);
            scannerRef.current = scanner;

            await scanner.start(
                { facingMode: 'environment' },
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1,
                },
                (decodedText) => {
                    lookupOrder(decodedText);
                },
                () => {}
            );
            setScanning(true);
        } catch (err: any) {
            console.error('Camera error (attempt ' + (retryCount + 1) + '):', err);
            // Retry up to 2 times with a delay (camera permission may take a moment to propagate)
            if (retryCount < 2) {
                setTimeout(() => startScanner(retryCount + 1), 1000);
            } else {
                setError('Camera access denied or not available. Use manual entry below.');
            }
        }
    };

    const stopScanner = () => {
        if (scannerRef.current) {
            scannerRef.current.stop().catch(() => {});
            try { scannerRef.current.clear(); } catch (_) {}
            scannerRef.current = null;
        }
        setScanning(false);
    };

    useEffect(() => {
        // Small delay to allow Android WebView permission grant to propagate
        const timer = setTimeout(() => startScanner(), 300);
        return () => {
            clearTimeout(timer);
            stopScanner();
        };
    }, []);

    const handleManualLookup = (e: React.FormEvent) => {
        e.preventDefault();
        lookupOrder(manualInput);
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-2">
                        <Camera className="w-5 h-5 text-primary-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Scan Receipt</h3>
                    </div>
                    <button
                        onClick={() => { stopScanner(); onClose(); }}
                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Camera viewfinder */}
                <div className="relative bg-black">
                    <div
                        id={containerRef.current}
                        className="w-full"
                        style={{ minHeight: 300 }}
                    />
                    {!scanning && !error && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 className="w-8 h-8 text-white animate-spin" />
                        </div>
                    )}
                </div>

                {/* Error / Status */}
                {error && (
                    <div className="px-5 py-3 bg-red-50 border-t border-red-100">
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                {/* Loading overlay */}
                {loading && (
                    <div className="px-5 py-3 bg-blue-50 border-t border-blue-100 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                        <p className="text-sm text-blue-700 font-medium">Looking up order...</p>
                    </div>
                )}

                {/* Manual entry */}
                <div className="px-5 py-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Or enter order number manually:</p>
                    <form onSubmit={handleManualLookup} className="flex gap-2">
                        <input
                            type="text"
                            value={manualInput}
                            onChange={(e) => setManualInput(e.target.value)}
                            placeholder="e.g. ORD-20260504-001"
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-primary-500 focus:border-primary-500"
                            autoFocus={!!error}
                        />
                        <button
                            type="submit"
                            disabled={loading || !manualInput.trim()}
                            className="px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                        >
                            <Search className="w-4 h-4" />
                            Find
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default QRScanner;
